import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Plus, Save, Trash2, X, Pencil } from 'lucide-react';
import { tarifsApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { formatMontant } from '../../utils/calculs';

function TarifRow({ tarif, onEdit, onDelete }) {
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50">
      <td className="px-4 py-3 font-medium text-gray-900">{tarif.label || '—'}</td>
      <td className="px-4 py-3 text-gray-600">
        {tarif.type === 'horaire' ? 'Tarif horaire' : 'Forfait'}
      </td>
      <td className="px-4 py-3 text-gray-600">
        {tarif.heures_min != null ? `${tarif.heures_min}h` : '—'}
      </td>
      <td className="px-4 py-3 text-gray-600">
        {tarif.heures_max != null ? `${tarif.heures_max}h` : '—'}
      </td>
      <td className="px-4 py-3">
        <span className="text-lg font-bold text-navy-700">{formatMontant(tarif.tarif)}</span>
        {tarif.type === 'horaire' && <span className="text-xs text-gray-400">/h</span>}
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          tarif.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {tarif.actif ? 'Actif' : 'Inactif'}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => onEdit(tarif)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-navy-700">
            <Pencil size={14} />
          </button>
          <button onClick={() => onDelete(tarif.id)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function TarifForm({ tarif, structures, onSaved, onCancel }) {
  const [form, setForm] = useState(tarif || {
    type: 'horaire', heures_min: '', heures_max: '', tarif: '', label: '', structure_id: '', actif: true,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.tarif) { alert('Le tarif est requis'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        structure_id: form.structure_id || null,
        heures_min: form.heures_min !== '' ? parseFloat(form.heures_min) : null,
        heures_max: form.heures_max !== '' ? parseFloat(form.heures_max) : null,
        tarif: parseFloat(form.tarif),
        actif: form.actif,
      };
      if (tarif?.id) {
        await tarifsApi.update(tarif.id, payload);
      } else {
        await tarifsApi.create(payload);
      }
      onSaved();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  return (
    <div className="card p-5 border-2 border-navy-200 bg-navy-50/30 space-y-4">
      <h4 className="font-semibold text-navy-700">
        {tarif?.id ? 'Modifier le tarif' : 'Nouveau tarif'}
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Type</label>
          <select value={form.type} onChange={e => upd('type', e.target.value)} className="form-select">
            <option value="horaire">Tarif horaire</option>
            <option value="forfait">Forfait</option>
          </select>
        </div>
        <div>
          <label className="form-label">Label</label>
          <input className="form-input" placeholder="Ex: 10h à 21h" value={form.label || ''} onChange={e => upd('label', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Structure (optionnel)</label>
          <select value={form.structure_id || ''} onChange={e => upd('structure_id', e.target.value)} className="form-select">
            <option value="">Toutes (global)</option>
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        {form.type === 'horaire' && (
          <>
            <div>
              <label className="form-label">Heures min</label>
              <input type="number" step="0.01" className="form-input" placeholder="3"
                value={form.heures_min || ''} onChange={e => upd('heures_min', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Heures max</label>
              <input type="number" step="0.01" className="form-input" placeholder="9.99"
                value={form.heures_max || ''} onChange={e => upd('heures_max', e.target.value)} />
            </div>
          </>
        )}
        <div>
          <label className="form-label">Tarif ({form.type === 'horaire' ? '€/h' : '€/mois'})</label>
          <input type="number" step="0.01" className="form-input" placeholder="10.50"
            value={form.tarif || ''} onChange={e => upd('tarif', e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" id="tarif_actif" checked={form.actif} onChange={e => upd('actif', e.target.checked)}
          className="w-4 h-4 accent-blue-500" />
        <label htmlFor="tarif_actif" className="text-sm text-gray-700">Tarif actif</label>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button onClick={onCancel} className="btn-secondary">
          <X size={14} /> Annuler
        </button>
      </div>
    </div>
  );
}

export default function TarifsPage() {
  const { structures, getApiParams } = useApp();
  const [tarifs, setTarifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTarif, setEditingTarif] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    tarifsApi.getAll(getApiParams()).then(res => setTarifs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce tarif ?')) return;
    try {
      await tarifsApi.delete(id);
      load();
    } catch (err) {
      alert('Erreur');
    }
  };

  const handleEdit = (tarif) => {
    setEditingTarif(tarif);
    setShowForm(true);
  };

  const horairesTarifs = tarifs.filter(t => t.type === 'horaire');
  const forfaitsTarifs = tarifs.filter(t => t.type === 'forfait');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <DollarSign size={20} className="text-emerald-500" /> Grille tarifaire
        </h2>
        <button onClick={() => { setEditingTarif(null); setShowForm(true); }} className="btn-primary">
          <Plus size={16} /> Nouveau tarif
        </button>
      </div>

      {showForm && (
        <TarifForm
          tarif={editingTarif}
          structures={structures}
          onSaved={() => { setShowForm(false); setEditingTarif(null); load(); }}
          onCancel={() => { setShowForm(false); setEditingTarif(null); }}
        />
      )}

      {/* Tarifs horaires */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <h3 className="font-semibold text-gray-700 text-sm">Tarifs horaires par tranches d'heures</h3>
        </div>
        {loading ? (
          <div className="py-12 text-center text-gray-400">Chargement...</div>
        ) : horairesTarifs.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">Aucun tarif horaire configuré</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Tranche</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Type</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">H. min</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">H. max</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Tarif</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Statut</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {horairesTarifs.map(t => (
                <TarifRow key={t.id} tarif={t} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Forfaits */}
      {forfaitsTarifs.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="font-semibold text-gray-700 text-sm">Tarifs forfaitaires</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Label</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Type</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">H. min</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">H. max</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Tarif</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Statut</th>
                <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {forfaitsTarifs.map(t => (
                <TarifRow key={t.id} tarif={t} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Visual preview */}
      {horairesTarifs.length > 0 && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-700 text-sm mb-3">Aperçu visuel de la grille</h3>
          <div className="flex items-end gap-1 h-40">
            {horairesTarifs.filter(t => t.actif).map(t => {
              const maxTarif = Math.max(...horairesTarifs.map(x => x.tarif));
              const height = maxTarif > 0 ? (t.tarif / maxTarif) * 100 : 0;
              return (
                <div key={t.id} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold text-navy-700">{t.tarif}€</span>
                  <div className="w-full bg-navy-400 rounded-t-lg transition-all"
                    style={{ height: `${height}%`, minHeight: '4px' }} />
                  <span className="text-[10px] text-gray-500 text-center leading-tight">{t.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
